import React, { useState } from 'react';
import { Tooltip, IconButton, useTheme } from '@mui/material';
import HelpIcon from '@mui/icons-material/HelpOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import { Control } from './ControlRenderer';

interface ControlIconsProps {
    control: Control;
    errorMessage?: string;
    warningMessage?: string;
    offsetRight?: number;
}

const ControlIcons: React.FC<ControlIconsProps> = ({ control, errorMessage, warningMessage, offsetRight }) => {
    const theme = useTheme();
    const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);
    const [errorTooltipOpen, setErrorTooltipOpen] = useState(false);
    const [warningTooltipOpen, setWarningTooltipOpen] = useState(false);

    const hasHelp = !!(control.helpText || control.helpLink);
    const hasError = !!errorMessage;
    const hasWarning = !hasError && !!warningMessage;
    const rightOffset = offsetRight ?? 0;

    // If nothing to show, render nothing
    if (!hasHelp && !hasError && !hasWarning) return null;

    const baseIconButtonSx = {
        position: 'absolute',
        top: 0,
        padding: 0,
        zIndex: 2,
        '&:hover': { backgroundColor: 'transparent' }
    };

    const commonTooltipProps = {
        disableHoverListener: true,
        placement: "top-end" as const,
        arrow: true,
        PopperProps: {
            modifiers: [{ name: 'offset', options: { offset: [0, -10] } }],
        },
    };

    return (
        <>
            {/* 1. Error Icon Logic */}
            {hasError && (
                <Tooltip
                    title={errorMessage}
                    open={errorTooltipOpen}
                    onClose={() => setErrorTooltipOpen(false)}
                    {...commonTooltipProps}
                    componentsProps={{
                        tooltip: {
                            sx: {
                                color: theme.palette.error.main,
                                backgroundColor: theme.palette.common.white,
                                border: `1px solid ${theme.palette.error.main}`,
                                fontSize: '1rem',
                                fontWeight: 500
                            }
                        },
                        arrow: { sx: { color: theme.palette.error.main } }
                    }}
                >
                    <IconButton
                        aria-label="Show error message"
                        onClick={(e) => {
                            e.stopPropagation();
                            setErrorTooltipOpen(!errorTooltipOpen);
                        }}
                        sx={{
                            ...baseIconButtonSx,
                            color: theme.palette.error.main,
                            // Offset to the left if the help icon is also there
                            right: rightOffset + (hasHelp ? 15 : 0),
                        }}
                        size="small"
                    >
                        <ErrorIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}


            {hasWarning && (
                <Tooltip
                    title={warningMessage}
                    open={warningTooltipOpen}
                    onClose={() => setWarningTooltipOpen(false)}
                    {...commonTooltipProps}
                    componentsProps={{
                        tooltip: {
                            sx: {
                                color: theme.palette.warning.dark,
                                backgroundColor: theme.palette.common.white,
                                border: `1px solid ${theme.palette.warning.main}`,
                                fontSize: '1rem',
                                fontWeight: 500
                            }
                        },
                        arrow: { sx: { color: theme.palette.warning.main } }
                    }}
                >
                    <IconButton
                        aria-label="Show warning message"
                        onClick={(e) => {
                            e.stopPropagation();
                            setWarningTooltipOpen(!warningTooltipOpen);
                        }}
                        sx={{
                            ...baseIconButtonSx,
                            color: theme.palette.warning.main,
                            right: rightOffset + (hasHelp ? 15 : 0),
                        }}
                        size="small"
                    >
                        <WarningIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {/* 2. Help Icon Logic */}
            {hasHelp && (
                control.helpText ? (
                    <Tooltip
                        title={control.helpText}
                        open={helpTooltipOpen}
                        onClose={() => setHelpTooltipOpen(false)}
                        {...commonTooltipProps}
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    color: theme.palette.info.main,
                                    backgroundColor: theme.palette.common.white,
                                    border: `1px solid ${theme.palette.info.main}`,
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    maxWidth: 300
                                }
                            },
                            arrow: { sx: { color: theme.palette.info.main } }
                        }}
                    >
                        <IconButton
                            aria-label="Show help message"
                            onClick={(e) => {
                                e.stopPropagation();
                                setHelpTooltipOpen(!helpTooltipOpen);
                            }}
                            sx={{ ...baseIconButtonSx, right: rightOffset, color: theme.palette.info.main }}
                            size="small"
                        >
                            <HelpIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <IconButton
                        aria-label="Open help link"
                        component="a"
                        href={control.helpLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ ...baseIconButtonSx, right: rightOffset, color: theme.palette.info.main }}
                        size="small"
                    >
                        <HelpIcon fontSize="small" />
                    </IconButton>
                )
            )}
        </>
    );
};

export default ControlIcons;